import { LayoutGrid, Notification, Section } from "@in/component-library";
import React, { FC, useEffect, useState } from "react";
import { sendSiteimproveEvent } from "../../../utils/sendSiteimproveEvent";
import { ApiException } from "../../apis/api-error";
import { useLayout } from "../../hooks/use-layout";
import { Application } from "../../models/application";
import { ApplicationStatus } from "../../models/application-status";
import { FormType } from "../../models/form-type";
import Card from "../card/card";
import Loader from "../loader/loader";

interface ApplicationListProps {
  applications?: Application[];
  heading: string;
  error?: ApiException;
  errorMessage?: string;
}

const OngoingCases: FC<ApplicationListProps> = ({ applications, heading, error, errorMessage }) => {
  const { translations } = useLayout();

  const [applicationList, setApplicationList] = useState<Application[]>();

  const statusMessage = (statusType: string) => {
    return statusType === ApplicationStatus.Draft ? translations[`applicationsStatus${statusType}`] : undefined;
  };

  const handleOnclick = (applicationTypeDescription: string) => {
    sendSiteimproveEvent("Applications in progress", "GoToLink", applicationTypeDescription);
  };

  const lastChangeText = (changeState: string) => {
    const changeStateText = applications?.find((status) => status.changeState === "Changed");
    changeState = translations[`applicationsLastChange${changeStateText!.changeState}`];
    return changeState;
  };

  useEffect(() => {
    const sortedDraftApplications = applications
      ? applications
          .filter((status) => status.statusType === ApplicationStatus.Draft)
          .map((application) => {
            return {
              ...application,
              lastChange: application.lastChange.replace(/^(.{2}).(.{2}).(.{4})$/, "$3-$2-$1")
            };
          })
          .sort((a, b) => new Date(b.lastChange).valueOf() - new Date(a.lastChange).valueOf())
      : [];

    const uniqueApplicationTypes = Array.from(new Set(sortedDraftApplications.map((item) => item.applicationType)));

    const activeDraftApplications: Application[] = uniqueApplicationTypes.map((applicationType) => {
      const data = sortedDraftApplications.find((application) => application.applicationType === applicationType);
      return data as Application;
    });

    setApplicationList(
      [...activeDraftApplications].sort((a, b) => {
        return new Date(b.lastChange).valueOf() - new Date(a.lastChange).valueOf();
      })
    );
  }, [applications]);

  const cards = applicationList?.map((application) => {
    const dateFormated = application.lastChange.replace(/^(.{4})-(.{2})-(.{2})(.*)/, "$3.$2.$1");

    return (
      <Card
        key={application.projectUrl}
        href={application.projectUrl}
        title={application.title}
        onClick={() => handleOnclick(application.applicationTypeDescription)}
        secondaryTitle={
          application.formType !== FormType.Unknown
            ? translations[`formType${application.formType}`]
            : application.applicationTypeDescription
        }
        footerLabel={statusMessage(application.statusType)}
        footerIcon="fa-lock-open"
        footerLabelBackground
        footerContent={`${lastChangeText(application.changeState)} ${dateFormated}`}
      />
    );
  });

  return applicationList && applicationList.length > 0 ? (
    <Section collapse className="drafted-applications-category">
      <h2 dangerouslySetInnerHTML={{ __html: heading }} />
      <LayoutGrid className="drafted-applications fade-in">{cards}</LayoutGrid>
    </Section>
  ) : error ? (
    <Section collapse className="drafted-applications-category">
      <h2 dangerouslySetInnerHTML={{ __html: heading }} />
      <Notification fullWidth type="error">
        {errorMessage}
      </Notification>
    </Section>
  ) : applications === undefined ? (
    <Section bleed>
      <div className="card-loader">
        <Loader />
      </div>
    </Section>
  ) : null;
};

export default OngoingCases;
